import React, { useContext } from 'react'
import styled from '@emotion/styled'
import { bool, number, object, string } from 'prop-types'
import Media from 'react-media'
import { JsonLd } from 'react-schemaorg'

//Import Components
import {
	AdsenseContext,
	DfpAdUnitSetup,
	EmailSubscribeContext,
	Layout,
	QtWidget,
	SimpleHeader,
	SiteConfigContext,
} from '@jelly/components'
import { EvergreenArticles } from './EvergreenArticles'
import { EvergreenBestPicks } from './EvergreenBestPicks'
import { RiverOfLinks } from './RiverOfLinks'
import { TrendingArticles } from './TrendingArticles'

//Styled Components via Emotion
const HubWrapper = styled.div`
	width: 100%;
`
const BodyWrapper = styled.div`
	display: block;
	height: 100%;
	margin: auto;
	width: 90%;

	h2 {
		color: ${({ theme }) => theme.hubTemplate.articleTitle};
		font-size: 28px;
	}

	${({ theme }) => theme.MQ.M} {
		margin-top: 2rem;
		width: 95%;
	}

	${({ theme }) => theme.MQ.XL} {
		${({ theme }) => theme.maxWidth.L};
		width: 100%;
	}
`
const CuratedContent = styled.div`
	${({ isSponsored, theme }) => isSponsored && `border-top: 3px solid ${theme.colors.lightGrey};`};
	display: grid;
	grid-gap: 1rem;
	grid-template-columns: 1fr;
	height: 100%;
	width: 100%;
`
const AdContainer = styled.div`
	grid-column: span 2;
	grid-row: 5;
	margin: 0 auto 2rem auto;
`
const QtWrapper = styled.div`
	grid-column: span 2;
	grid-row: 3;
`

Hub.propTypes = {
	canonical: string,
	channels: object.isRequired,
	href: string,
	isCurated: bool,
	moduleArticles: object.isRequired,
	moduleBestPicks: object,
	moduleTrendingArticles: object,
	moduleRiverOfLinks: object,
	page: number,
	path: string,
	qtOfferGroupId: string,
	sponsor: object,
}

/**
 * This is the Hub Page index, pulls together the whole hub page
 * If pagination is not on the first page, curated content shouldn't show
 *
 * @param {Object} props.channels :: breadcrumb data (primary and option sub channels aka hub)
 * @param {String} props.href :: name of the hub
 * @param {Boolean} props.isCurated :: flag to determine if the hub page is curated or not
 * @param {Object} props.moduleArticles :: curated data for the Evergreen Articles section
 * @param {Object} props.moduleBestPicks :: curated data for the Evergreen Best Picks section
 * @param {Object} props.moduleTrendingArticles :: curated data for the Trending Articles section
 * @param {Object} props.moduleRiverOfLinks :: data provided by swifttype of Articles, Best Picks, and Reviews
 * @param {Number} props.page :: current page
 * @param {String} props.path :: the path of the page we are on
 * @param {String} props.qtOfferGroupId :: Offer Group ID needed by the QT widget
 * @param {Object} props.sponsor :: sponsored data, company name, logo, etc
 */
const HubPageDisplay = ({
	channels,
	isCurated,
	href,
	moduleArticles,
	moduleBestPicks,
	moduleTrendingArticles,
	moduleRiverOfLinks,
	page = 1,
	path,
	qtOfferGroupId,
	showLatestLink,
	sponsor,
}) => {
	const isSponsored = !!sponsor
	const isPage1 = page === 1 || page === '1'
	const { mainContent = [], search } = moduleRiverOfLinks

	const { adsenseMediaQuery, hasAdsense, hasAdsenseMobile } = useContext(AdsenseContext)

	const {
		siteConstants: {
			EMAIL_CAPTURE_SOURCES: { hub },
		},
	} = useContext(SiteConfigContext)
	// Enable scroll listener here so the Email Capture Floater Bar pops up on scroll
	const { emailSubscribeFormSubmitted, setEmailSubscribeSource, setIsScrollListenerEnabled } = useContext(
		EmailSubscribeContext
	)

	setEmailSubscribeSource(hub)

	if (!isSponsored && !emailSubscribeFormSubmitted) {
		setIsScrollListenerEnabled(true)
	}

	// Extracting the StyledComponent as well as the QtWidget component so we can inject it into the mainContent below
	const qtWidget = qtOfferGroupId && (
		<QtWrapper>
			<QtWidget qtOfferGroupId={qtOfferGroupId} />
		</QtWrapper>
	)

	// On page 1 of subhubs, we want the qt widget to display as the 4th item in the list.
	let content = mainContent.content
	if (!isCurated && isPage1) {
		content = [
			...mainContent.content.slice(0, 3),
			{ qtWidget, doc_type: 'qtWidget' },
			...mainContent.content.slice(3, mainContent.content.length - 1),
		]
	}

	return (
		<>
			{isCurated && isPage1 && (
				<CuratedContent isSponsored={isSponsored}>
					<EvergreenArticles sponsor={sponsor} {...moduleArticles} />

					{/* QT widget and wrapper have been extracted above */}
					{qtWidget}

					{moduleBestPicks && <EvergreenBestPicks {...moduleBestPicks} />}

					{(hasAdsense || hasAdsenseMobile) && (
						<Media query={adsenseMediaQuery} defaultMatches={false}>
							{(matches) => matches && <AdContainer id="in-article-vca-2" />}
						</Media>
					)}
					<TrendingArticles sponsor={sponsor} {...moduleTrendingArticles} />
				</CuratedContent>
			)}
			<RiverOfLinks
				numberOfPages={search.num_pages}
				page={page}
				path={path}
				results={content}
				isSponsored={isSponsored}
				showLatestLink={showLatestLink}
				{...moduleRiverOfLinks}
			/>
			{hasAdsense && (
				<Media query={adsenseMediaQuery} defaultMatches={false}>
					{(matches) => matches && <DfpAdUnitSetup channels={channels} docType="hub" guid={href} />}
				</Media>
			)}
		</>
	)
}

/**
 * This is the Hub Page index, pulls together the whole hub page
 * If pagination is not on the first page, curated content shouldn't show
 *
 * @param {Object} props.channels :: breadcrumb data (primary and option sub channels aka hub)
 * @param {String} props.canonical :: url path of the current page
 * @param {Object} props.meta :: seo meta data for the page
 * @param {Object} props.sponsor :: sponsored data, company name, logo, etc
 * @param {String} props.structureData :: Schema data for google structured data
 * @param {String} props.subtitle :: subtitle copy for hub page
 * @param {String} props.title :: title copy for the hub page
 */
export function Hub(props) {
	const { channels, canonical, path, meta, sponsor, structuredData, subtitle, title } = props

	return (
		<HubWrapper>
			<Layout inverseLinkColors={true} meta={meta} hasAdDisclosure={true}>
				{structuredData && <JsonLd item={structuredData} />}
				<SimpleHeader
					breadcrumbs={channels}
					canonical={canonical}
					isHubTemplate={true}
					sponsor={sponsor}
					shouldShowLeaderboardAds={true}
					subTitle={subtitle}
					title={title}
				/>
				<BodyWrapper>
					<HubPageDisplay path={path} {...props} />
				</BodyWrapper>
			</Layout>
		</HubWrapper>
	)
}
